/**
 * useRenameRoteiro — updates the `request_metadata.normalized.topic` field
 * on an `ai_jobs` row scoped to the current user.
 *
 * Roteiros have no standalone title column; the user-visible name is derived
 * from `request_metadata->normalized->topic`. We merge-patch that path so
 * other metadata fields (type, objective, etc.) are preserved.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSupabaseClient } from "@/lib/supabase";
import { useProfileId } from "@/features/onboarding/hooks/useProfileId";

interface RenameRoteiroInput {
  id: string;
  topic: string;
}

export function useRenameRoteiro() {
  const qc = useQueryClient();
  const { client } = useSupabaseClient();
  const { profileId } = useProfileId();

  return useMutation<void, Error, RenameRoteiroInput>({
    mutationFn: async ({ id, topic }) => {
      if (!profileId) throw new Error("Sessão não pronta. Tente novamente.");

      // First fetch the current request_metadata to avoid clobbering other fields
      const { data, error: fetchError } = await client
        .from("ai_jobs")
        .select("request_metadata")
        .eq("id", id)
        .eq("user_id", profileId)
        .single();

      if (fetchError) throw new Error(fetchError.message);

      const existing = (data?.request_metadata ?? {}) as Record<string, unknown>;
      const normalized = ((existing.normalized ?? {}) as Record<string, unknown>);

      const updated = {
        ...existing,
        normalized: {
          ...normalized,
          topic,
        },
      };

      const { error } = await client
        .from("ai_jobs")
        .update({ request_metadata: updated })
        .eq("id", id)
        .eq("user_id", profileId);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ai_jobs"] });
      void qc.invalidateQueries({ queryKey: ["roteiros"] });
    },
  });
}
